import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStaffAuth } from './StaffAuthContext';
import { requestedStaffAdminPath, staffAdminReturnState } from './staffAdminReturnPath';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .mv-root {
    min-height: 100vh;
    background: #080B12;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Sans', sans-serif;
    padding: 24px;
    position: relative;
    overflow: hidden;
  }

  .mv-bg-grid {
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(rgba(201,168,76,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(201,168,76,0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    pointer-events: none;
  }

  .mv-card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 420px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 20px;
    padding: 48px 36px;
    backdrop-filter: blur(20px);
    animation: mv-in 0.5s ease both;
  }

  @keyframes mv-in {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .mv-icon {
    width: 56px;
    height: 56px;
    background: rgba(201,168,76,0.1);
    border: 1px solid rgba(201,168,76,0.25);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
  }

  .mv-title {
    font-family: 'Syne', sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: #fff;
    text-align: center;
    margin-bottom: 8px;
    letter-spacing: -0.4px;
  }

  .mv-sub {
    font-size: 13px;
    color: rgba(255,255,255,0.4);
    text-align: center;
    line-height: 1.6;
    margin-bottom: 36px;
  }

  .mv-otp-inputs {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-bottom: 8px;
  }

  .mv-otp-input {
    width: 52px;
    height: 64px;
    text-align: center;
    font-family: 'Syne', sans-serif;
    font-size: 26px;
    font-weight: 700;
    color: #fff;
    background: rgba(255,255,255,0.05);
    border: 2px solid rgba(255,255,255,0.1);
    border-radius: 12px;
    outline: none;
    transition: border-color 0.2s, background 0.2s;
    caret-color: #C9A84C;
  }

  .mv-otp-input:focus {
    border-color: #C9A84C;
    background: rgba(201,168,76,0.06);
  }

  .mv-otp-input.error { border-color: rgba(239,68,68,0.7); }
  .mv-otp-input.success { border-color: rgba(74,222,128,0.7); }

  .mv-hint {
    font-size: 12px;
    color: rgba(255,255,255,0.25);
    text-align: center;
    margin-bottom: 28px;
  }

  .mv-btn {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #C9A84C, #A0832E);
    border: none;
    border-radius: 10px;
    font-family: 'Syne', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #080B12;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.1s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .mv-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  .mv-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .mv-error {
    padding: 12px 16px;
    background: rgba(239,68,68,0.1);
    border: 1px solid rgba(239,68,68,0.25);
    border-radius: 10px;
    font-size: 13px;
    color: #FCA5A5;
    text-align: center;
    margin-bottom: 16px;
  }

  .mv-back {
    display: block;
    text-align: center;
    margin-top: 20px;
    font-size: 13px;
    color: rgba(255,255,255,0.3);
    cursor: pointer;
    transition: color 0.2s;
    background: none;
    border: none;
    width: 100%;
    padding: 8px;
  }
  .mv-back:hover { color: rgba(255,255,255,0.6); }

  .mv-recovery {
    display: block;
    text-align: center;
    margin-top: 12px;
    font-size: 12px;
    color: rgba(201,168,76,0.5);
    cursor: pointer;
    background: none;
    border: none;
    width: 100%;
    padding: 4px;
    transition: color 0.2s;
  }
  .mv-recovery:hover { color: rgba(201,168,76,0.8); }

  .mv-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(8,11,18,0.3);
    border-top-color: #080B12;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .mv-success-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 16px 0;
  }

  .mv-success-icon {
    width: 56px;
    height: 56px;
    background: rgba(74,222,128,0.1);
    border: 1px solid rgba(74,222,128,0.3);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: mv-pop 0.4s ease both;
  }

  @keyframes mv-pop {
    from { transform: scale(0.7); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  }

  @media (max-width: 440px) {
    .mv-card { padding: 36px 20px; }
    .mv-otp-input { width: 44px; height: 56px; font-size: 22px; }
  }
`;

export default function StaffMFAVerify() {
  const [otp,     setOtp]     = useState(['','','','','','']);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef([]);
  const verifiedRef = useRef(false);
  const { staffMFAVerify, pendingUserId } = useStaffAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = requestedStaffAdminPath(location);


  useEffect(() => {
    // Only redirect to login if pendingUserId is missing AND we have not
    // just successfully verified. Without this guard, clearing pendingUserId
    // after successful MFA causes an immediate redirect back to login.
    if (!pendingUserId && !verifiedRef.current) {
      navigate('/admin/login', { replace: true, state: staffAdminReturnState(returnTo) });
      return;
    }
    if (pendingUserId) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [pendingUserId, navigate, returnTo]);

  const handleChange = (i, val) => {
    const v = val.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    setError('');
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
    if (i === 5 && v) setTimeout(() => handleVerify([...next]), 0);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const arr = pasted.split('');
      setOtp(arr);
      setError('');
      inputRefs.current[5]?.focus();
      setTimeout(() => handleVerify(arr), 0);
    }
  };

  const handleVerify = async (currentOtp) => {
    const code = (currentOtp || otp).join('');
    if (code.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      const data = await staffMFAVerify(code);
      setSuccess(true);
      verifiedRef.current = true;
      setTimeout(() => {
        if (data.role_selection_required) {
          navigate('/admin/select-role', { replace: true, state: staffAdminReturnState(returnTo) });
        } else {
          navigate(returnTo, { replace: true });
        }
      }, 800);
    } catch (err) {
      const msg = err?.data?.error || '';
      if (msg.includes('Too many')) {
        setError('Too many failed attempts. Please wait before trying again.');
      } else {
        setError('Incorrect code. Please check your authenticator and try again.');
      }
      setOtp(['','','','','','']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="mv-root">
        <div className="mv-bg-grid" />
        <div className="mv-card">

          {success ? (
            <div className="mv-success-state">
              <div className="mv-success-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, fontWeight: 600, color: '#4ADE80' }}>Verified</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>Redirecting to Admin...</div>
            </div>
          ) : (
            <>
              <div className="mv-icon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.5">
                  <rect x="5" y="11" width="14" height="10" rx="2" ry="2"/>
                  <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
                </svg>
              </div>

              <h1 className="mv-title">Two-Factor Authentication</h1>
              <p className="mv-sub">Enter the 6-digit code from your authenticator app.</p>

              {error && <div className="mv-error">{error}</div>}

              <div className="mv-otp-inputs" onPaste={handlePaste}>
                {otp.map((val, i) => (
                  <input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    className={`mv-otp-input${error ? ' error' : ''}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={val}
                    onChange={e => handleChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    disabled={loading}
                  />
                ))}
              </div>

              <p className="mv-hint">Code refreshes every 30 seconds in your authenticator app.</p>

              <button className="mv-btn" onClick={() => handleVerify()} disabled={loading || otp.join('').length !== 6}>
                {loading ? <><div className="mv-spinner" /> Verifying...</> : 'Verify & Login'}
              </button>

              <button className="mv-back" onClick={() => navigate('/admin/login', { replace: true, state: staffAdminReturnState(returnTo) })}>
                Back to login
              </button>
              <button className="mv-recovery" onClick={() => navigate('/admin/recovery', { replace: true })}>
                Use a recovery code instead
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
